import { differenceInCalendarDays } from 'date-fns';
import { createDateSafe } from '@/utils/dateUtils';
import { overlapRange } from './vacationTeamOverlap';
import type { VacationSegment, VacationConflict } from '../models/vacation.types';

export interface ConflictRange {
    start: Date;
    end: Date;
    days: number;
}

export function getConflictRanges(
    segments: VacationSegment[] | undefined | null,
    conflict: VacationConflict
): ConflictRange[] {
    if (!segments || segments.length === 0) return [];

    const allocationStart = createDateSafe(conflict.startDate);
    const allocationEnd = conflict.endDate ? createDateSafe(conflict.endDate) : null;

    if (!allocationStart) return [];

    const ranges: ConflictRange[] = [];

    segments.forEach((segment) => {
        const segStart = createDateSafe(segment.startDate);
        const segEnd = createDateSafe(segment.endDate);

        if (!segStart || !segEnd) return;

        const overlap = overlapRange(
            segStart,
            segEnd,
            allocationStart,
            allocationEnd || segEnd
        );

        if (overlap) {
            ranges.push({
                start: overlap.start,
                end: overlap.end,
                days: overlap.days,
            });
        }
    });

    if (ranges.length === 0) return [];

    return mergeAdjacentRanges(ranges);
}

function mergeAdjacentRanges(ranges: ConflictRange[]): ConflictRange[] {
    if (ranges.length === 0) return [];

    const sorted = [...ranges].sort((a, b) => a.start.getTime() - b.start.getTime());
    const merged: ConflictRange[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
        const current = sorted[i];
        const last = merged[merged.length - 1];

        const daysBetween = differenceInCalendarDays(current.start, last.end);
        if (daysBetween <= 1) {
            if (current.end > last.end) {
                last.end = current.end;
                last.days = differenceInCalendarDays(last.end, last.start) + 1;
            }
        } else {
            merged.push(current);
        }
    }

    return merged;
}
